/**
 * 업무절차 디자인 — 단계 카드 (원형 노드 + 카드 + 펼침 패널 + 하위 단계).
 *
 * 업무절차.html 의 카드 마크업을 그대로 옮기고, 기존 운영 프로세스 데이터의
 * 필드(내용/일자/비고/카테고리/관련 문서/첨부/준비사항 메모)를 같은 시각
 * 언어로 패널 안에 배치했다. 텍스트 입력은 uncontrolled + blur 커밋이라
 * 키 입력마다 저장 API 를 부르지 않는다.
 *
 * 색상은 workflowTokens(theme.palette.mode) — 앱 다크/라이트 토글을 따른다.
 * 모든 네이티브 입력은 box-sizing: border-box 로 카드 테두리와 겹치지 않는다.
 */
import React, { useRef } from 'react';
import { Box, useTheme } from '@mui/material';

import type {
  OperationChecklistItem,
  OperationProcessChildRow,
  OperationProcessRow,
} from '../../services/OperationProcessService';
import StepAttachmentsButton from './StepAttachmentsButton';
import { normalizeDocLinkHref } from './OperationDocLinksCell';
import { CATEGORIES } from './operationProcessShared';
import {
  WfTokens,
  WorkflowChildPatch,
  WorkflowRowPatch,
  WorkflowStepView,
  WorkflowUnitRef,
  categoryLabel,
  checklistOf,
  childrenOf,
  workflowTokens,
} from './operationWorkflowModel';

/** 앱 테마 모드에 맞는 업무절차 디자인 토큰. */
export function useWorkflowTokens(): WfTokens {
  const theme = useTheme();
  return workflowTokens(theme.palette.mode);
}

export interface WorkflowActions {
  toggleExpand: (rowId: string) => void;
  startEdit: (id: string) => void;
  commitTitle: (ref: WorkflowUnitRef, value: string) => void;
  patchRow: (rowId: string, patch: WorkflowRowPatch) => void;
  patchChild: (rowId: string, childId: string, patch: WorkflowChildPatch) => void;
  setDone: (ref: WorkflowUnitRef, done: boolean) => void;
  toggleCheckItem: (ref: WorkflowUnitRef, itemId: string) => void;
  addCheckItem: (ref: WorkflowUnitRef, text: string) => void;
  removeCheckItem: (ref: WorkflowUnitRef, itemId: string) => void;
  addSub: (rowId: string) => void;
  removeStep: (rowId: string) => void;
  removeSub: (rowId: string, childId: string) => void;
  moveStep: (rowId: string, dir: -1 | 1) => void;
  moveSub: (rowId: string, childId: string, dir: -1 | 1) => void;
}

interface WorkflowStepCardProps {
  step: WorkflowStepView;
  stepCount: number;
  /** 현재 진행 unit 이 이 행의 하위일 때 그 childId. */
  currentChildId: string | null;
  expanded: boolean;
  editingId: string | null;
  attachmentsProjectId?: string;
  attachmentCounts: Record<string, number>;
  onAttachmentsChanged: () => void;
  actions: WorkflowActions;
}

// ─── 저수준 프리미티브 (업무절차 디자인 토큰) ────────────────────────────────

const textInputSx = (wf: WfTokens) =>
  ({
    boxSizing: 'border-box',
    fontFamily: wf.font,
    fontSize: 13,
    px: '10px',
    py: '5px',
    border: `1px solid ${wf.line}`,
    borderRadius: '6px',
    outline: 'none',
    bgcolor: wf.bg,
    color: wf.ink,
    '&::placeholder': { color: wf.faint },
    '&:focus': { borderColor: wf.acc },
  }) as const;

const headingSx = (wf: WfTokens) =>
  ({ mt: '14px', fontSize: 12, fontWeight: 700, color: wf.muted }) as const;

interface WFieldProps {
  label: string;
  width?: number | string;
  flex?: boolean;
  small?: boolean;
  defaultValue: string;
  placeholder?: string;
  onCommit: (value: string) => void;
}

/** 라벨 + 단일행 입력 (blur/Enter 커밋). 업무절차의 담당자 입력 스타일. */
function WField({ label, width, flex, small, defaultValue, placeholder, onCommit }: WFieldProps) {
  const wf = useWorkflowTokens();
  const commit = (el: HTMLInputElement) => {
    if (el.value !== defaultValue) onCommit(el.value);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, ...(flex && { flex: 1, minWidth: 140 }) }}>
      <Box component="span" sx={{ fontSize: small ? 11 : 12, fontWeight: 700, color: wf.muted, flexShrink: 0 }}>
        {label}
      </Box>
      <Box
        component="input"
        defaultValue={defaultValue}
        placeholder={placeholder}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => commit(e.currentTarget)}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        sx={{ ...textInputSx(wf), fontSize: small ? 12 : 13, width: width ?? 120, ...(flex && { width: '100%' }) }}
      />
    </Box>
  );
}

interface WTextareaProps {
  defaultValue: string;
  placeholder?: string;
  small?: boolean;
  onCommit: (value: string) => void;
}

/** 여러 줄 내용 입력 (blur 커밋). 줄 수에 맞춰 초기 높이 조절. */
function WTextarea({ defaultValue, placeholder, small, onCommit }: WTextareaProps) {
  const wf = useWorkflowTokens();
  const rows = Math.min(6, Math.max(small ? 1 : 2, defaultValue.split('\n').length));
  return (
    <Box
      component="textarea"
      defaultValue={defaultValue}
      placeholder={placeholder}
      rows={rows}
      onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (e.currentTarget.value !== defaultValue) onCommit(e.currentTarget.value);
      }}
      sx={{
        ...textInputSx(wf),
        fontSize: small ? 12.5 : 13,
        width: '100%',
        display: 'block',
        resize: 'vertical',
        lineHeight: 1.55,
      }}
    />
  );
}

interface WButtonProps {
  tone?: 'primary' | 'outline' | 'danger' | 'muted';
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

/** 업무절차의 액션 버튼 4종 톤. */
function WButton({ tone = 'outline', disabled, onClick, children }: WButtonProps) {
  const wf = useWorkflowTokens();
  const byTone = {
    primary: { fontWeight: 700, border: 'none', bgcolor: wf.acc, color: '#fff' },
    muted: { fontWeight: 700, border: 'none', bgcolor: wf.chipBg, color: wf.muted },
    outline: { fontWeight: 500, border: `1px solid ${wf.lineSoft}`, bgcolor: wf.paper, color: wf.btnInk },
    danger: { fontWeight: 500, border: `1px solid ${wf.lineSoft}`, bgcolor: wf.paper, color: wf.danger },
  }[tone];
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        fontFamily: wf.font,
        fontSize: 12,
        px: '14px',
        py: '7px',
        borderRadius: '7px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        ...byTone,
      }}
    >
      {children}
    </Box>
  );
}

/** × 삭제 글리프 (hover 시 붉게). */
function RemoveGlyph({ onClick, size = 14 }: { onClick: (e: React.MouseEvent) => void; size?: number }) {
  const wf = useWorkflowTokens();
  return (
    <Box
      component="span"
      className="wf-editctl"
      onClick={onClick}
      sx={{ cursor: 'pointer', color: wf.ghost, fontSize: size, px: '4px', '&:hover': { color: wf.danger } }}
    >
      ×
    </Box>
  );
}

/** ↑↓ 순서 이동 글리프 (디자인에 없는 최소 확장 — 템플릿 순서 편집용). */
function MoveGlyphs({ onMove, canUp, canDown }: { onMove: (dir: -1 | 1) => void; canUp: boolean; canDown: boolean }) {
  const wf = useWorkflowTokens();
  const glyph = (dir: -1 | 1, enabled: boolean, label: string) => (
    <Box
      component="span"
      className="wf-editctl"
      title={label}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (enabled) onMove(dir);
      }}
      sx={{
        cursor: enabled ? 'pointer' : 'default',
        color: enabled ? wf.ghost : wf.lineSub,
        fontSize: 13,
        px: '2px',
        '&:hover': enabled ? { color: wf.subInk } : undefined,
      }}
    >
      {dir === -1 ? '↑' : '↓'}
    </Box>
  );
  return (
    <>
      {glyph(-1, canUp, '위로')}
      {glyph(1, canDown, '아래로')}
    </>
  );
}

// ─── 체크리스트 ──────────────────────────────────────────────────────────────

interface ChecklistBlockProps {
  items: OperationChecklistItem[];
  refU: WorkflowUnitRef;
  small?: boolean;
  addPlaceholder: string;
  actions: WorkflowActions;
}

function ChecklistBlock({ items, refU, small, addPlaceholder, actions }: ChecklistBlockProps) {
  const wf = useWorkflowTokens();
  const box = small ? 14 : 16;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: small ? '5px' : '6px', mt: '8px' }}>
      {items.map((ck) => (
        <Box key={ck.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={ck.done}
            onChange={() => actions.toggleCheckItem(refU, ck.id)}
            style={{ width: box, height: box, accentColor: wf.acc, flexShrink: 0 }}
          />
          <Box
            component="span"
            sx={{
              flex: 1,
              fontSize: small ? 13 : 14,
              color: ck.done ? wf.faint : wf.body,
              textDecoration: ck.done ? 'line-through' : 'none',
            }}
          >
            {ck.text}
          </Box>
          <RemoveGlyph size={small ? 13 : 14} onClick={() => actions.removeCheckItem(refU, ck.id)} />
        </Box>
      ))}
      <Box
        component="input"
        className="wf-editctl"
        placeholder={addPlaceholder}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== 'Enter') return;
          const v = e.currentTarget.value.trim();
          if (!v) return;
          e.currentTarget.value = '';
          actions.addCheckItem(refU, v);
        }}
        sx={{
          boxSizing: 'border-box',
          fontFamily: wf.font,
          fontSize: small ? 12 : 13,
          px: small ? '9px' : '10px',
          py: small ? '5px' : '6px',
          border: `1px dashed ${wf.dash}`,
          borderRadius: '6px',
          outline: 'none',
          bgcolor: 'transparent',
          color: wf.ink,
          maxWidth: '100%',
          width: small ? 200 : 220,
          '&::placeholder': { color: wf.faint },
          '&:focus': { borderColor: wf.acc },
        }}
      />
    </Box>
  );
}

// ─── 관련 문서 링크 ──────────────────────────────────────────────────────────

function DocLinksBlock({ row, actions }: { row: OperationProcessRow; actions: WorkflowActions }) {
  const wf = useWorkflowTokens();
  const labelRef = useRef<HTMLInputElement | null>(null);
  const urlRef = useRef<HTMLInputElement | null>(null);
  const links = Array.isArray(row.docLinks) ? row.docLinks : [];

  const add = () => {
    const url = (urlRef.current?.value ?? '').trim();
    if (!url) return;
    const label = (labelRef.current?.value ?? '').trim();
    actions.patchRow(row.id, { docLinks: [...links, { label, url }] });
    if (labelRef.current) labelRef.current.value = '';
    if (urlRef.current) urlRef.current.value = '';
  };

  const dashedSx = {
    ...textInputSx(wf),
    fontSize: 12,
    border: `1px dashed ${wf.dash}`,
    bgcolor: 'transparent',
  } as const;

  return (
    <>
      <Box sx={headingSx(wf)}>관련 문서</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', mt: '8px' }}>
        {links
          .filter((l) => (l.url ?? '').trim())
          .map((l, i) => (
            <Box key={`${l.url}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box
                component="a"
                href={normalizeDocLinkHref(l.url)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 13, color: wf.accDeep, wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
              >
                {(l.label ?? '').trim() || l.url}
              </Box>
              <RemoveGlyph
                onClick={() => actions.patchRow(row.id, { docLinks: links.filter((_, j) => j !== i) })}
              />
            </Box>
          ))}
        <Box className="wf-editctl" sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <Box component="input" ref={labelRef} placeholder="이름(선택)" sx={{ ...dashedSx, width: 110 }} />
          <Box
            component="input"
            ref={urlRef}
            placeholder="https://... 입력 후 Enter"
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') add();
            }}
            sx={{ ...dashedSx, width: 240, maxWidth: '100%' }}
          />
        </Box>
      </Box>
    </>
  );
}

// ─── 하위 단계 카드 ──────────────────────────────────────────────────────────

interface SubStepCardProps {
  row: OperationProcessRow;
  child: OperationProcessChildRow;
  num: string;
  isCurrent: boolean;
  canUp: boolean;
  canDown: boolean;
  editing: boolean;
  attachmentsProjectId?: string;
  attachmentCount: number;
  onAttachmentsChanged: () => void;
  actions: WorkflowActions;
}

function SubStepCard({
  row,
  child,
  num,
  isCurrent,
  canUp,
  canDown,
  editing,
  attachmentsProjectId,
  attachmentCount,
  onAttachmentsChanged,
  actions,
}: SubStepCardProps) {
  const wf = useWorkflowTokens();
  const refU: WorkflowUnitRef = { rowId: row.id, childId: child.id };
  const worker = (child.worker ?? '').trim();
  return (
    <Box
      sx={{
        border: isCurrent ? `1.5px solid ${wf.acc}` : `1px solid ${wf.lineSub}`,
        borderRadius: '10px',
        p: '12px 14px',
        bgcolor: isCurrent ? wf.subCurBg : wf.bg,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <input
          type="checkbox"
          checked={!!child.checked}
          onChange={() => actions.setDone(refU, !child.checked)}
          style={{ width: 16, height: 16, accentColor: wf.acc, flexShrink: 0 }}
        />
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 900,
            color: wf.accDeep,
            bgcolor: wf.accPale,
            borderRadius: '4px',
            p: '2px 6px',
            flexShrink: 0,
          }}
        >
          {num}
        </Box>
        {editing ? (
          <Box
            component="input"
            defaultValue={child.name}
            autoFocus
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => actions.commitTitle(refU, e.currentTarget.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            sx={{
              boxSizing: 'border-box',
              flex: 1,
              minWidth: 0,
              fontFamily: wf.font,
              fontSize: 14,
              fontWeight: 500,
              p: '3px 6px',
              border: `1px solid ${wf.acc}`,
              borderRadius: '5px',
              outline: 'none',
              bgcolor: wf.paper,
              color: wf.ink,
            }}
          />
        ) : (
          <Box
            component="span"
            onClick={() => actions.startEdit(child.id)}
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'text',
              color: child.checked ? wf.faint : wf.body,
              textDecoration: child.checked ? 'line-through' : 'none',
            }}
          >
            {child.name || '하위 단계'}
          </Box>
        )}
        {isCurrent && (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 700,
              bgcolor: wf.inverseBg,
              color: wf.inverseLabel,
              borderRadius: '999px',
              p: '3px 8px',
              flexShrink: 0,
            }}
          >
            진행 중
          </Box>
        )}
        {worker && (
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: wf.subInk,
              bgcolor: wf.chipBg,
              borderRadius: '999px',
              p: '2px 8px',
              flexShrink: 0,
            }}
          >
            {worker}
          </Box>
        )}
        <MoveGlyphs canUp={canUp} canDown={canDown} onMove={(dir) => actions.moveSub(row.id, child.id, dir)} />
        <RemoveGlyph
          onClick={() => {
            if (!window.confirm('이 하위 단계를 삭제할까요?')) return;
            actions.removeSub(row.id, child.id);
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '8px', pl: '26px' }}>
        <WTextarea
          small
          defaultValue={child.content ?? ''}
          placeholder="내용"
          onCommit={(v) => actions.patchChild(row.id, child.id, { content: v })}
        />
        <ChecklistBlock
          items={checklistOf(child)}
          refU={refU}
          small
          addPlaceholder="+ 준비물 추가 후 Enter"
          actions={actions}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <WField
            small
            label="담당자"
            width={96}
            defaultValue={child.worker ?? ''}
            placeholder="이름"
            onCommit={(v) => actions.patchChild(row.id, child.id, { worker: v })}
          />
          <WField
            small
            label="일자"
            width={84}
            defaultValue={child.date ?? ''}
            placeholder="YY-MM-DD"
            onCommit={(v) => actions.patchChild(row.id, child.id, { date: v })}
          />
          <WField
            small
            label="비고"
            flex
            defaultValue={child.remark ?? ''}
            onCommit={(v) => actions.patchChild(row.id, child.id, { remark: v })}
          />
          {attachmentsProjectId && (
            <StepAttachmentsButton
              projectId={attachmentsProjectId}
              stepId={child.id}
              stepLabel={child.name || child.id}
              editable
              count={attachmentCount}
              onChanged={onAttachmentsChanged}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── 단계 카드 (노드 + 카드) ─────────────────────────────────────────────────

const statusBadge = (wf: WfTokens) =>
  ({
    done: { label: '완료', bg: wf.accPale, fg: wf.accDeep },
    current: { label: '진행 중', bg: wf.inverseBg, fg: wf.inverseLabel },
    next: { label: '다음', bg: wf.nextBg, fg: wf.nextFg },
    todo: { label: '예정', bg: wf.chipBg, fg: wf.muted },
  }) as const;

export default function WorkflowStepCard({
  step,
  stepCount,
  currentChildId,
  expanded,
  editingId,
  attachmentsProjectId,
  attachmentCounts,
  onAttachmentsChanged,
  actions,
}: WorkflowStepCardProps) {
  const wf = useWorkflowTokens();
  const { row, index, status, done } = step;
  const children = childrenOf(row);
  const hasSubs = children.length > 0;
  const badge = statusBadge(wf)[status];
  const rowRef: WorkflowUnitRef = { rowId: row.id };
  const editingTitle = editingId === row.id;
  const worker = (row.worker ?? '').trim();

  const nodeBg = done ? wf.acc : status === 'current' ? wf.inverseBg : wf.paper;
  const nodeFg = done ? '#fff' : status === 'current' ? wf.inverseFg : wf.faint;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 0 }}>
      {/* 원형 노드 + 세로 연결선 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 900,
            flexShrink: 0,
            boxSizing: 'border-box',
            bgcolor: nodeBg,
            color: nodeFg,
            border: done || status === 'current' ? 'none' : `2px solid ${wf.lineSoft}`,
          }}
        >
          {done ? '✓' : index + 1}
        </Box>
        <Box sx={{ width: '2px', flex: 1, bgcolor: wf.lineSoft, minHeight: 16 }} />
      </Box>

      <Box sx={{ p: '0 0 20px 12px', minWidth: 0 }}>
        <Box
          sx={{
            bgcolor: wf.paper,
            borderRadius: '12px',
            border: status === 'current' ? `2px solid ${wf.acc}` : `1px solid ${wf.line}`,
            boxShadow: '0 1px 3px rgba(32,31,28,0.05)',
            overflow: 'hidden',
          }}
        >
          {/* 카드 헤더 — 클릭으로 펼침/접힘 */}
          <Box
            onClick={() => actions.toggleExpand(row.id)}
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '16px 18px', cursor: 'pointer', flexWrap: 'wrap' }}
          >
            {editingTitle ? (
              <Box
                component="input"
                defaultValue={row.name}
                autoFocus
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => actions.commitTitle(rowRef, e.currentTarget.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                sx={{
                  boxSizing: 'border-box',
                  flex: 1,
                  minWidth: 120,
                  fontFamily: wf.font,
                  fontSize: 17,
                  fontWeight: 700,
                  p: '4px 8px',
                  border: `1px solid ${wf.acc}`,
                  borderRadius: '6px',
                  outline: 'none',
                  bgcolor: wf.paper,
                  color: wf.ink,
                }}
              />
            ) : (
              <Box sx={{ flex: 1, fontSize: 17, fontWeight: 700, minWidth: 120, color: wf.ink }}>
                {row.name || `${categoryLabel(row.category)} #${index + 1}`}
              </Box>
            )}
            <Box
              component="span"
              sx={{ fontSize: 12, fontWeight: 500, color: wf.subInk, bgcolor: wf.chipBg, borderRadius: '999px', p: '4px 10px', flexShrink: 0 }}
            >
              {categoryLabel(row.category)}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: 12, fontWeight: 500, color: wf.subInk, bgcolor: wf.chipBg, borderRadius: '999px', p: '4px 10px', flexShrink: 0 }}
            >
              {worker || '미지정'}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.05em',
                borderRadius: '999px',
                p: '4px 10px',
                bgcolor: badge.bg,
                color: badge.fg,
                flexShrink: 0,
              }}
            >
              {badge.label}
            </Box>
            <Box sx={{ fontSize: 12, color: wf.faint }}>{expanded ? '▲' : '▼'}</Box>
          </Box>

          {/* 펼침 패널 */}
          {expanded && (
            <Box sx={{ p: '0 18px 16px', borderTop: `1px solid ${wf.lineFaint}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mt: '14px', flexWrap: 'wrap' }}>
                <WField
                  label="담당자"
                  defaultValue={row.worker ?? ''}
                  placeholder="이름 입력"
                  onCommit={(v) => actions.patchRow(row.id, { worker: v })}
                />
                <WField
                  label="일자"
                  width={100}
                  defaultValue={row.date ?? ''}
                  placeholder="YY-MM-DD"
                  onCommit={(v) => actions.patchRow(row.id, { date: v })}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: wf.muted }}>
                    카테고리
                  </Box>
                  <Box
                    component="select"
                    value={row.category}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      actions.patchRow(row.id, { category: e.target.value })
                    }
                    sx={{ ...textInputSx(wf), fontSize: 12, py: '4px', cursor: 'pointer' }}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Box>
                </Box>
                <WField
                  label="비고"
                  flex
                  defaultValue={row.remark ?? ''}
                  onCommit={(v) => actions.patchRow(row.id, { remark: v })}
                />
              </Box>

              <Box sx={headingSx(wf)}>내용</Box>
              <Box sx={{ mt: '8px' }}>
                <WTextarea
                  defaultValue={row.content ?? ''}
                  placeholder="내용"
                  onCommit={(v) => actions.patchRow(row.id, { content: v })}
                />
              </Box>

              <Box sx={headingSx(wf)}>준비물 · 체크리스트</Box>
              <Box sx={{ mt: '8px' }}>
                <Box
                  component="input"
                  defaultValue={row.preparation ?? ''}
                  placeholder="준비사항 메모"
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    if (e.currentTarget.value !== (row.preparation ?? ''))
                      actions.patchRow(row.id, { preparation: e.currentTarget.value });
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                  sx={{ ...textInputSx(wf), width: '100%', color: wf.subInk }}
                />
              </Box>
              <ChecklistBlock
                items={checklistOf(row)}
                refU={rowRef}
                addPlaceholder="+ 항목 추가 후 Enter"
                actions={actions}
              />

              <DocLinksBlock row={row} actions={actions} />

              {hasSubs && (
                <Box sx={{ mt: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {children.map((child, ci) => (
                    <SubStepCard
                      key={child.id}
                      row={row}
                      child={child}
                      num={`${index + 1}-${ci + 1}`}
                      isCurrent={currentChildId === child.id}
                      canUp={ci > 0}
                      canDown={ci < children.length - 1}
                      editing={editingId === child.id}
                      attachmentsProjectId={attachmentsProjectId}
                      attachmentCount={attachmentCounts[child.id] ?? 0}
                      onAttachmentsChanged={onAttachmentsChanged}
                      actions={actions}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '16px', flexWrap: 'wrap' }}>
                <WButton
                  tone={done ? 'muted' : 'primary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    // 하위 단계가 있으면 하위 체크로 진행 (업무절차 디자인 규칙).
                    if (hasSubs) return;
                    actions.setDone(rowRef, !row.checked);
                  }}
                >
                  {hasSubs ? (done ? '모두 완료됨' : '하위 단계로 진행') : done ? '완료 취소' : '완료 처리'}
                </WButton>
                <WButton onClick={() => actions.addSub(row.id)}>+ 하위 단계</WButton>
                <WButton onClick={() => actions.startEdit(row.id)}>이름 수정</WButton>
                <WButton disabled={index === 0} onClick={() => actions.moveStep(row.id, -1)}>
                  위로
                </WButton>
                <WButton disabled={index === stepCount - 1} onClick={() => actions.moveStep(row.id, 1)}>
                  아래로
                </WButton>
                <WButton
                  tone="danger"
                  onClick={() => {
                    if (!window.confirm('이 단계를 삭제할까요?')) return;
                    actions.removeStep(row.id);
                  }}
                >
                  삭제
                </WButton>
                {attachmentsProjectId && (
                  <StepAttachmentsButton
                    projectId={attachmentsProjectId}
                    stepId={row.id}
                    stepLabel={row.name || row.id}
                    editable
                    count={attachmentCounts[row.id] ?? 0}
                    onChanged={onAttachmentsChanged}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
